import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Tooltip, Checkbox } from "antd";
import { getEquityPoolUsage } from "../redux/actions/CompanyAction";
import {
  emdash,
  RadioSelector,
  toDollar,
} from "../utils/Constants/systemSetting";
import { exportToCSV } from "../utils/ExecutiveData/csvExport";
import CohortSelector from "./ExecutiveOverview/CohortSection/CohortSelector";
import CustomTitleElement from "./OptionsCalculators/utils/CustomTitleElement";
import getCIKtickerMap from "../utils/Constants/getCIKtickerMap";
import { CitationLink } from "./CitationLink";

export default function EquityPoolUsage({
  companyInfo,
  FY_Options,
  PDFprocessing,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.UserReducer);
  const { subscription } = useSelector((state) => state.CustomerReducer);
  const isTier2 = useMemo(() => {
    if (user?.tier2access || subscription?.plan?.metadata?.isTier2 === "true") {
      return true;
    }
    return false;
  }, [user, subscription]);

  const { companies } = useSelector((state) => state.CompaniesReducer);

  const equityPoolAccess = useMemo(() => {
    if (subscription?.plan?.metadata?.equityPoolAccess === "true") {
      return true;
    }
    return false;
  }, [subscription]);

  const [reportFiscalYear, setReportFiscalYear] = useState("FY 2025");

  const [FYmode, setFYmode] = useState(true);

  const outStandingShareOptions = ["Beginning", "End", "Average"];
  const [outstandingShareMode, setOutstandingShareMode] = useState("End"); // Beginning, End or Average

  const outStandingShareDilutionOptions = ["Non-Diluted", "Diluted"];
  const [outstandingShareDilutionMode, setOutstandingShareDilutionMode] =
    useState("Non-Diluted"); // Beginning, End or Average

  const averageDataOptions = ["Selected FY", "3-Year Average"];
  const [averageDataMode, setAverageDataMode] = useState("Selected FY"); // Beginning, End or Average

  const [EquityPoolAverageData, setEquityPoolAverageData] = useState({});

  const tickersWith3yearOption = ["MS", "KMI"];

  // useEffect(() => {
  //   setReportFiscalYear("FY 2025");
  // }, []);

  const FYsNeeded = useMemo(() => {
    const FYsNeeded = [];
    if (FYmode && reportFiscalYear.includes("FY ")) {
      const numericalFY = parseInt(reportFiscalYear.replace("FY ", ""));
      for (let i = numericalFY; i > numericalFY - 3; i--) {
        FYsNeeded.push(i);
      }
    }
    return FYsNeeded;
  }, [FYmode, reportFiscalYear]);

  // useMemo(() => {
  //   setEquityPoolAverageData({});
  // }, [reportFiscalYear]);

  useEffect(() => {
    setFYmode(reportFiscalYear.includes("FY "));
  }, [reportFiscalYear]);

  function addSpaceBetweenCapitalizedWords(str) {
    return str.replace(/([a-z])([A-Z])/g, "$1 $2");
  }
  function toTitleCase(str) {
    return str.replace(
      /\w\S*/g,
      (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
    );
  }

  const [cohortTableTickers, setCohortTableTickers] = useState([]);
  const [cohortSelected, setCohortSelected] = useState("None");

  const { equityPoolUsage: allEquityPoolUsage } = useSelector(
    (state) => state.CompanyReducer,
  );

  const burnRateCalculationTypes = [
    { label: "RSUs", value: "RestrictedStockUnits" },
    { label: "PSUs", value: "PerformanceShareUnits" },
    { label: "Options", value: "StockOptions" },
    // { label: "Other", value: "OtherAwards" },
    { label: "All Awards", value: "allAwards" },
  ];

  const [burnRateCalculationType, setBurnRateCalculationType] = useState(
    burnRateCalculationTypes.map((item) => item.value),
  );

  const [allAwardsAccountedFor, setAllAwardsAccountedFor] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("ACCESS_TOKEN");
    const ticker = companyInfo?.Ticker;
    const CIK = companyInfo?.CIK;

    const CIKtickerMap = getCIKtickerMap(
      [ticker, ...cohortTableTickers.filter((item) => item !== ticker)],
      companies,
    );
    if (ticker && !cohortTableTickers.length) {
      dispatch(
        getEquityPoolUsage(
          JSON.stringify([{ ticker, CIK }]),
          reportFiscalYear,
          token,
        ),
      );
    } else if (ticker && cohortTableTickers.length) {
      dispatch(getEquityPoolUsage(CIKtickerMap, reportFiscalYear, token));
    }
  }, [companies, companyInfo, reportFiscalYear, cohortTableTickers, dispatch]);

  // useMemo(() => {
  //   const token = localStorage.getItem("ACCESS_TOKEN");
  //   const ticker = companyInfo?.Ticker;
  //   if (cohortTableTickers.length) {
  //     dispatch(
  //       getEquityPoolUsage(
  //         [ticker, ...cohortTableTickers.filter((item) => item !== ticker)],
  //         token
  //       )
  //     );
  //   }
  // }, [companyInfo, cohortTableTickers, dispatch]);

  const getEquityPoolColumns = useCallback(
    (isPeerGroupTable = false) => {
      const defaultColumns = [
        {
          title: "Award Type",
          dataIndex: "award",
          key: "award",
          align: "center",
          render: (item) => addSpaceBetweenCapitalizedWords(item),
          sorter: (a, b) => (a?.award || "").localeCompare(b?.award || ""),
        },
        {
          title: "Granted",
          dataIndex: "Granted",
          key: "Granted",
          align: "center",
          render: (item) => (item ? item.toLocaleString() : emdash),
          sorter: (a, b) =>
            parseInt(a?.Granted || 0) - parseInt(b?.Granted || 0),
        },
        {
          title: "Vested",
          dataIndex: "Vested",
          key: "Vested",
          align: "center",
          render: (item) => (item ? item.toLocaleString() : emdash),
          sorter: (a, b) => parseInt(a?.Vested || 0) - parseInt(b?.Vested || 0),
        },
        {
          title: "Exercised",
          dataIndex: "Exercised",
          key: "Exercised",
          align: "center",
          render: (item) => (item ? item.toLocaleString() : emdash),
          sorter: (a, b) =>
            parseInt(a?.Exercised || 0) - parseInt(b?.Exercised || 0),
        },
        {
          title: "Forfeited",
          dataIndex: "Forfited",
          key: "Forfited",
          align: "center",
          render: (item) => (item ? item.toLocaleString() : emdash),
          sorter: (a, b) =>
            parseInt(a?.Forfited || 0) - parseInt(b?.Forfited || 0),
        },
        {
          title: "Outstanding",
          dataIndex: "Outstanding",
          key: "Outstanding",
          align: "center",
          render: (item) => (item ? item.toLocaleString() : emdash),
          sorter: (a, b) =>
            parseInt(a?.Outstanding || 0) - parseInt(b?.Outstanding || 0),
        },
      ];
      if (!isPeerGroupTable) {
        defaultColumns.push({
          title: "Additional Details",
          dataIndex: "AdditionalDetails",
          key: "AdditionalDetails",
          align: "center",
        });
      }

      if (isPeerGroupTable) {
        defaultColumns.unshift({
          title: "Ticker",
          dataIndex: "Ticker",
          key: "Ticker",
          align: "center",
          sorter: (a, b) => (a?.Ticker || "").localeCompare(b?.Ticker || ""),
        });
      }

      defaultColumns.push({
        title: "Citation",
        dataIndex: "Citation",
        key: "Citation",
        align: "center",
        render: (citation, col) => (
          <CitationLink
            citation={citation}
            ticker={col?.Ticker}
            reportYear={reportFiscalYear}
          />
        ),
      });
      return defaultColumns;
    },
    [reportFiscalYear],
  );

  const peerGroupOverviewColumns = [
    {
      title: "Ticker",
      dataIndex: "Ticker",
      key: "Ticker",
      align: "center",
      sorter: (a, b) => (a?.Ticker || "").localeCompare(b?.Ticker || ""),
    },
    {
      title: "Total Granted",
      dataIndex: "TotalGranted",
      key: "TotalGranted",
      align: "center",
      render: (item) => (item ? item.toLocaleString() : emdash),
      sorter: (a, b) =>
        parseInt(a?.TotalGranted || 0) - parseInt(b?.TotalGranted || 0),
    },
    {
      title: "Total Equity Outstanding",
      dataIndex: "TotalOutstanding",
      key: "TotalOutstanding",
      align: "center",
      render: (item) => (item ? item.toLocaleString() : emdash),
      sorter: (a, b) =>
        parseInt(a?.TotalOutstanding || 0) - parseInt(b?.TotalOutstanding || 0),
    },
    {
      title: "Total Common Shares Outstanding",
      dataIndex: "TotalCommonOutstanding",
      key: "TotalCommonOutstanding",
      align: "center",
      render: (item) => (item ? item.toLocaleString() : emdash),
      sorter: (a, b) =>
        parseInt(a?.TotalCommonOutstanding || 0) -
        parseInt(b?.TotalCommonOutstanding || 0),
    },
    {
      title: "Burn Rate (%)",
      dataIndex: "BurnRate",
      key: "BurnRate",
      render: (item) => (!isNaN(item) ? item : emdash),
      align: "center",
      sorter: (a, b) =>
        parseFloat(a?.BurnRate || 0) - parseFloat(b?.BurnRate || 0),
    },
    {
      title: "Plan Overhang (%)",
      dataIndex: "PlanOverhang",
      key: "PlanOverhang",
      render: (item) => (!isNaN(item) ? item : emdash),
      align: "center",
      sorter: (a, b) =>
        parseFloat(a?.PlanOverhang || 0) - parseFloat(b?.PlanOverhang || 0),
    },
    {
      title: "Citation",
      dataIndex: "Citation",
      key: "Citation",
      align: "center",
      render: (citation, col) => (
        <CitationLink
          citation={citation}
          ticker={col?.Ticker}
          reportYear={reportFiscalYear}
        />
      ),
    },
  ];

  const peerGroupAverageColumns = useMemo(() => {
    const defaultColumns = [
      {
        title: "Ticker",
        dataIndex: "Ticker",
        key: "Ticker",
        align: "center",
        sorter: (a, b) => (a?.Ticker || "").localeCompare(b?.Ticker || ""),
      },
    ];
    for (const FY of [...FYsNeeded].reverse()) {
      defaultColumns.push({
        title: `${FY} Burn Rate (%)`,
        dataIndex: `${FY}BurnRate`,
        key: `${FY}BurnRate`,
        render: (item) => (item ? item : emdash),
        align: "center",
        sorter: (a, b) =>
          (parseFloat(a?.[`${FY}BurnRate`]) || 0) -
          (parseFloat(b?.[`${FY}BurnRate`]) || 0),
      });
    }
    defaultColumns.push({
      title: `Average Burn Rate (%)`,
      dataIndex: `AverageBurnRate`,
      key: `AverageBurnRate`,
      align: "center",
      render: (item) => <b>{item}</b>,
      sorter: (a, b) =>
        (parseFloat(a?.[`AverageBurnRate`]) || 0) -
        (parseFloat(b?.[`AverageBurnRate`]) || 0),
    });
    for (const FY of [...FYsNeeded].reverse()) {
      defaultColumns.push({
        title: `${FY} Plan Overhang (%)`,
        dataIndex: `${FY}PlanOverhang`,
        key: `${FY}PlanOverhang`,
        align: "center",
        render: (item) => (item ? item : emdash),
        sorter: (a, b) =>
          (parseFloat(a?.[`${FY}PlanOverhang`]) || 0) -
          (parseFloat(b?.[`${FY}PlanOverhang`]) || 0),
      });
    }
    defaultColumns.push({
      title: `Average Plan Overhang (%)`,
      dataIndex: `AveragePlanOverhang`,
      key: `AveragePlanOverhang`,
      render: (item) => <b>{item}</b>,
      align: "center",
      sorter: (a, b) =>
        (parseFloat(a?.[`AveragePlanOverhang`]) || 0) -
        (parseFloat(b?.[`AveragePlanOverhang`]) || 0),
    });
    return defaultColumns;
  }, [FYsNeeded]);

  const [burnRate, setBurnRate] = useState(null);
  const [adjustedBurnRate, setAdjustedBurnRate] = useState(null);
  const [planOverhang, setPlanOverhang] = useState(null);
  const [EquityPoolData, setEquityPoolData] = useState(null);

  const getEquityPoolUsageData = useCallback(
    (FYneeded = null, ticker = null) => {
      const equityPoolUsage =
        allEquityPoolUsage?.[FYneeded || reportFiscalYear.replace("FY ", "")];

      if (!equityPoolUsage?.[ticker || companyInfo?.Ticker]) return [];
      return [equityPoolUsage?.[ticker || companyInfo?.Ticker]];
    },
    [companyInfo, allEquityPoolUsage, reportFiscalYear],
  );

  const getShareUsageData = useCallback(
    (FYneeded = null, ticker = null) => {
      const equityPoolUsageData = getEquityPoolUsageData(FYneeded, ticker);
      return equityPoolUsageData[0]?.ShareUsageData?.[0]
        ? equityPoolUsageData[0]?.ShareUsageData?.[0]
        : FYmode && equityPoolUsageData[0]?.ShareUsageData
          ? equityPoolUsageData[0]?.ShareUsageData
          : {};
    },
    [getEquityPoolUsageData, FYmode],
  );

  const shareUsageData = getShareUsageData();

  const getAdjustedSharesOutstanding = (
    totalSharesOutstanding,
    outstandingShareDilutionMode,
    TotalOutstanding,
  ) => {
    let adjustedOutstandingShared = null;
    if (outstandingShareDilutionMode === "Diluted") {
      if (!isNaN(totalSharesOutstanding) && !isNaN(TotalOutstanding)) {
        adjustedOutstandingShared =
          parseInt(totalSharesOutstanding) + parseInt(TotalOutstanding);
      }
    }
    return adjustedOutstandingShared || totalSharesOutstanding;
  };

  const getSharesOutstanding = (
    FYmode,
    shareUsageData,
    outstandingShareMode,
  ) => {
    if (!FYmode) return shareUsageData?.TotalOutstaning;

    if (shareUsageData?.TotalOutstaning?.length > 0) {
      if (outstandingShareMode === "Average") {
        const sharesBeginning = shareUsageData?.TotalOutstaning.find(
          (item) =>
            typeof item === "object" &&
            Object.keys(item)?.includes(`TotalOutstaningPeriod${"Beginning"}`),
        )?.[`TotalOutstaningPeriod${"Beginning"}`];
        const sharesEnd = shareUsageData?.TotalOutstaning.find(
          (item) =>
            typeof item === "object" &&
            Object.keys(item)?.includes(`TotalOutstaningPeriod${"End"}`),
        )?.[`TotalOutstaningPeriod${"End"}`];
        if (!isNaN(sharesBeginning) && !isNaN(sharesEnd)) {
          return (parseInt(sharesBeginning) + parseInt(sharesEnd)) / 2;
        }
      }
      return shareUsageData?.TotalOutstaning.find(
        (item) =>
          typeof item === "object" &&
          Object.keys(item)?.includes(
            `TotalOutstaningPeriod${outstandingShareMode}`,
          ),
      )?.[`TotalOutstaningPeriod${outstandingShareMode}`];
    }
    return undefined;
  };

  const getSharesOutstandingData = (
    FYmode,
    shareUsageData,
    outstandingShareMode,
  ) => {
    if (!FYmode) return shareUsageData?.TotalOutstaning;

    if (shareUsageData?.TotalOutstaning?.length > 0) {
      if (outstandingShareMode === "Average") {
        const sharesBeginning = shareUsageData?.TotalOutstaning.find(
          (item) =>
            typeof item === "object" &&
            Object.keys(item)?.includes(`TotalOutstaningPeriod${"Beginning"}`),
        );
        const sharesEnd = shareUsageData?.TotalOutstaning.find(
          (item) =>
            typeof item === "object" &&
            Object.keys(item)?.includes(`TotalOutstaningPeriod${"End"}`),
        );
        if (!isNaN(sharesBeginning) && !isNaN(sharesEnd)) {
          return sharesEnd;
        }
      }
      return shareUsageData?.TotalOutstaning.find(
        (item) =>
          typeof item === "object" &&
          Object.keys(item)?.includes(
            `TotalOutstaningPeriod${outstandingShareMode}`,
          ),
      );
    }
    return undefined;
  };

  const getEquityPoolData = useCallback(
    (FYneeded = null) => {
      const shareUsageData = getShareUsageData(FYneeded);
      const currentData = !FYmode
        ? shareUsageData
        : shareUsageData?.EquityAwards;
      if (!currentData) return;

      const awardTypes = [
        "RestrictedStockUnits",
        "PerformanceShareUnits",
        "StockOptions",
      ];

      // Adds any award types not named explicity
      if (FYmode && typeof currentData === "object") {
        for (const awardType of Object.keys(currentData)) {
          if (
            ![...awardTypes].includes(awardType) &&
            currentData[awardType]?.Citation
          ) {
            awardTypes.push(awardType);
          }
        }
      }

      const formattedData = [];

      let TotalGranted = 0;
      let TotalOutstanding = 0;

      let keyID = 1;

      for (const awardType of awardTypes) {
        if (!currentData?.[awardType]) continue;
        keyID += 1;
        const newData = { award: awardType, key: keyID };
        if (!currentData?.[awardType]) continue;
        for (const [key, val] of Object.entries(currentData[awardType])) {
          newData[key] = val;
          if (key === "Granted" && val > 0) {
            if (
              burnRateCalculationType.includes("allAwards") ||
              burnRateCalculationType.includes(awardType)
            ) {
              TotalGranted += parseInt(val);
            }
          }
          if (key === "Outstanding" && val > 0) {
            if (
              burnRateCalculationType.includes("allAwards") ||
              burnRateCalculationType.includes(awardType)
            ) {
              TotalOutstanding += parseInt(val);
            }
          }
        }

        formattedData.push(newData);
      }

      const totalSharesOutstanding = getAdjustedSharesOutstanding(
        getSharesOutstanding(FYmode, shareUsageData, outstandingShareMode),
        outstandingShareDilutionMode,
        TotalOutstanding,
      );

      const SharesAuthorized = shareUsageData?.SharesAuthorized;

      const burnRate = parseFloat(
        (100 * TotalGranted) / totalSharesOutstanding,
      ).toFixed(2);

      // setBurnRate(
      //   parseFloat((100 * TotalGranted) / totalSharesOutstanding).toFixed(2)
      // );

      const adjustedBurnRate = parseFloat(
        (100 * TotalGranted + SharesAuthorized) / totalSharesOutstanding,
      ).toFixed(2);

      const planOverhang = parseFloat(
        (100 * TotalOutstanding) / totalSharesOutstanding,
      ).toFixed(2);

      // setAdjustedBurnRate(
      //   parseFloat(
      //     (100 * TotalGranted + SharesAuthorized) / totalSharesOutstanding
      //   ).toFixed(2)
      // );

      // setPlanOverhang(
      //   parseFloat((100 * TotalOutstanding) / totalSharesOutstanding).toFixed(2)
      // );

      // setEquityPoolData(formattedData);

      return {
        tableData: formattedData,
        burnRate,
        adjustedBurnRate,
        planOverhang,
      };
    },
    [
      FYmode,
      outstandingShareMode,
      outstandingShareDilutionMode,
      burnRateCalculationType,
      getShareUsageData,
    ],
  );

  const [EquityPoolPeerData, setEquityPoolPeerData] = useState(null);
  const [EquityPoolOverviewData, setEquityPoolOverviewData] = useState(null);

  const getEquityPoolPeerData = useCallback(
    (FYneeded) => {
      const formattedData2 = [];
      const formattedOverviewData = [];
      const equityPoolUsageFYneeded = allEquityPoolUsage[FYneeded];
      if (!equityPoolUsageFYneeded) return;

      for (const ticker of [
        companyInfo?.Ticker,
        ...cohortTableTickers.filter((item) => item !== companyInfo?.Ticker),
      ]) {
        // const currentData =
        //   equityPoolUsageFYneeded?.[ticker]?.ShareUsageData?.[0];
        const shareUsageData = getShareUsageData(FYneeded, ticker);
        const currentData = !FYmode
          ? shareUsageData
          : shareUsageData?.EquityAwards;

        if (!currentData) continue;

        const awardTypes = [
          "RestrictedStockUnits",
          "PerformanceShareUnits",
          "StockOptions",
          "OtherAwards",
        ];

        let TotalGranted = 0;
        let TotalOutstanding = 0;

        let keyID = 1;

        for (const awardType of awardTypes) {
          if (!currentData?.[awardType]?.Citation) continue;
          keyID += 1;
          const newData = {
            Ticker: ticker,
            award: awardType,
            key: ticker + keyID,
          };
          for (const [key, val] of Object.entries(currentData[awardType])) {
            newData[key] = val;
            if (key === "Granted" && val > 0) {
              if (
                burnRateCalculationType.includes("allAwards") ||
                burnRateCalculationType.includes(awardType)
              ) {
                TotalGranted += parseInt(val);
              }
            }
            if (key === "Outstanding" && val > 0) {
              if (
                burnRateCalculationType.includes("allAwards") ||
                burnRateCalculationType.includes(awardType)
              ) {
                TotalOutstanding += parseInt(val);
              }
            }
          }

          formattedData2.push(newData);
        }

        // const totalSharesOutstanding = getSharesOutstanding(
        //   FYmode,
        //   shareUsageData,
        //   outstandingShareDilutionMode
        // );

        const totalSharesOutstanding = getAdjustedSharesOutstanding(
          getSharesOutstanding(FYmode, shareUsageData, outstandingShareMode),
          outstandingShareDilutionMode,
          TotalOutstanding,
        );

        const totalSharesCitation =
          currentData?.TotalOutstaningCitation ||
          getSharesOutstandingData(FYmode, shareUsageData, outstandingShareMode)
            ?.Citation;

        const SharesAuthorized = currentData?.SharesAuthorized;

        const BurnRate = parseFloat(
          (100 * TotalGranted) / totalSharesOutstanding,
        ).toFixed(2);

        // setAdjustedBurnRate(
        //   parseFloat(
        //     (100 * TotalGranted + SharesAuthorized) / totalSharesOutstanding
        //   ).toFixed(2)
        // );

        const PlanOverhang = parseFloat(
          (100 * TotalOutstanding) / totalSharesOutstanding,
        ).toFixed(2);
        const newData = {
          Ticker: ticker,
          key: ticker,
        };

        newData["TotalGranted"] = TotalGranted || "";
        newData["TotalOutstanding"] = TotalOutstanding || "";
        newData["TotalCommonOutstanding"] = totalSharesOutstanding || "";
        newData["BurnRate"] = BurnRate || "";
        newData["PlanOverhang"] = PlanOverhang || "";
        newData["Citation"] = totalSharesCitation || "";

        formattedOverviewData.push(newData);
      }

      return { overviewData: formattedOverviewData, peerData: formattedData2 };
      // setEquityPoolOverviewData(formattedOverviewData);
      // setEquityPoolPeerData(formattedData2);
    },
    [
      companyInfo,
      allEquityPoolUsage,
      FYmode,
      outstandingShareDilutionMode,
      cohortTableTickers,
      outstandingShareMode,
      burnRateCalculationType,
      getShareUsageData,
    ],
  );

  const getEquityPoolAveragePeerData = useCallback(() => {
    const formattedData2 = [];
    const formattedOverviewData = [];
    const equityPoolUsageFYneeded =
      allEquityPoolUsage[reportFiscalYear.replace("FY ", "")];
    if (!equityPoolUsageFYneeded) return;

    for (const ticker of [
      companyInfo?.Ticker,
      ...cohortTableTickers.filter((item) => item !== companyInfo?.Ticker),
    ]) {
      const newData = {
        Ticker: ticker,
        key: ticker,
      };

      for (const FYneeded of FYsNeeded) {
        // const currentData =
        //   equityPoolUsageFYneeded?.[ticker]?.ShareUsageData?.[0];
        const shareUsageData = getShareUsageData(FYneeded, ticker);
        const currentData = !FYmode
          ? shareUsageData
          : shareUsageData?.EquityAwards;

        if (!currentData) continue;

        const awardTypes = [
          "RestrictedStockUnits",
          "PerformanceShareUnits",
          "StockOptions",
          "OtherAwards",
        ];

        let TotalGranted = 0;
        let TotalOutstanding = 0;

        let keyID = 1;

        for (const awardType of awardTypes) {
          if (!currentData?.[awardType]) continue;
          keyID += 1;
          const newData = {
            Ticker: ticker,
            award: awardType,
            key: ticker + keyID,
          };
          for (const [key, val] of Object.entries(currentData[awardType])) {
            newData[key] = val;
            if (key === "Granted" && val > 0) {
              if (
                burnRateCalculationType.includes("allAwards") ||
                burnRateCalculationType.includes(awardType)
              ) {
                TotalGranted += parseInt(val);
              }
            }

            if (key === "Outstanding" && val > 0) {
              if (
                burnRateCalculationType.includes("allAwards") ||
                burnRateCalculationType.includes(awardType)
              ) {
                TotalOutstanding += parseInt(val);
              }
            }
          }

          formattedData2.push(newData);
        }

        // const totalSharesOutstanding = getSharesOutstanding(
        //   FYmode,
        //   shareUsageData,
        //   outstandingShareDilutionMode
        // );

        const totalSharesOutstanding = getAdjustedSharesOutstanding(
          getSharesOutstanding(FYmode, shareUsageData, outstandingShareMode),
          outstandingShareDilutionMode,
          TotalOutstanding,
        );

        const totalSharesCitation =
          currentData?.TotalOutstaningCitation ||
          getSharesOutstandingData(FYmode, shareUsageData, outstandingShareMode)
            ?.Citation;

        const SharesAuthorized = currentData?.SharesAuthorized;

        const BurnRate = parseFloat(
          (100 * TotalGranted) / totalSharesOutstanding,
        ).toFixed(2);

        // setAdjustedBurnRate(
        //   parseFloat(
        //     (100 * TotalGranted + SharesAuthorized) / totalSharesOutstanding
        //   ).toFixed(2)
        // );

        const PlanOverhang = parseFloat(
          (100 * TotalOutstanding) / totalSharesOutstanding,
        ).toFixed(2);

        newData["TotalGranted"] = TotalGranted || "";
        newData["TotalOutstanding"] = TotalOutstanding || "";
        newData["TotalCommonOutstanding"] = totalSharesOutstanding || "";
        newData[`${FYneeded}BurnRate`] = !isNaN(BurnRate) ? BurnRate : "";
        newData[`${FYneeded}PlanOverhang`] = !isNaN(PlanOverhang)
          ? BurnRate
          : "";
        newData["Citation"] = totalSharesCitation || "";
      }

      for (const dataType of ["BurnRate", "PlanOverhang"]) {
        let total = 0;
        let dataCount = 0;

        for (const FY of FYsNeeded) {
          total += newData[`${FY}BurnRate`]
            ? parseFloat(newData[`${FY}BurnRate`])
            : 0;
          dataCount += newData[`${FY}BurnRate`] ? 1 : 0;
        }
        newData[`Average${dataType}`] =
          dataCount && !isNaN(total) ? (total / dataCount).toFixed(2) : "";
      }
      if (newData?.AverageBurnRate) {
        formattedOverviewData.push(newData);
      }
    }

    return { overviewData: formattedOverviewData, peerData: formattedData2 };

    // setEquityPoolOverviewData(formattedOverviewData);
    // setEquityPoolPeerData(formattedData2);
  }, [
    companyInfo,
    allEquityPoolUsage,
    FYmode,
    outstandingShareDilutionMode,
    cohortTableTickers,
    outstandingShareMode,
    FYsNeeded,
    reportFiscalYear,
    burnRateCalculationType,
    getShareUsageData,
  ]);

  function EquityPoolResults({ FYneeded = null }) {
    const equityPoolData = getEquityPoolData(FYneeded);

    if (FYneeded === "FY 2024") {
      FYneeded = "";
    }

    return (
      <div>
        {!equityPoolData?.burnRate ? (
          <h2>{FYneeded} Equity Pool Usage: N/A</h2>
        ) : (
          <div>
            <h2>{FYneeded} Equity Pool Usage</h2>
            {/* {!PDFprocessing && (
                  <div style={{ width: "100%" }}>
                    <Button
                      style={{ marginBottom: 10 }}
                      onClick={() =>
                        exportToCSV(
                          formatDataForCSVexport(),
                          `${companyInfo?.Ticker} ${
                            changeInControls?.[companyInfo?.Ticker]
                              ?.ChangeInControlDetails || ""
                          } Equity Pool Usage ${
                            new Date().toString().split("GMT-")[0]
                          }.csv`
                        )
                      }
                    >
                      Export Equity Pool Usage as CSV
                    </Button>
                  </div>
                )} */}
            <Table
              columns={getEquityPoolColumns()}
              dataSource={equityPoolData?.tableData}
              pagination={{ hideOnSinglePage: true, defaultPageSize: 25 }}
            />
            {getSharesOutstanding(
              FYmode,
              shareUsageData,
              outstandingShareMode,
            ) && (
              <p style={{ marginTop: 10 }}>
                <b>{FYneeded} Total Shares Outstaning: </b>

                {getAdjustedSharesOutstanding(
                  getSharesOutstanding(
                    FYmode,
                    shareUsageData,
                    outstandingShareMode,
                  ),
                  outstandingShareDilutionMode,
                  equityPoolData && Array.isArray(equityPoolData?.tableData)
                    ? equityPoolData?.tableData.reduce(
                        (prev, curr) =>
                          prev +
                          (typeof curr === "object"
                            ? curr?.Outstanding || 0
                            : 0),
                        0,
                      )
                    : null,
                )?.toLocaleString()}
              </p>
            )}
            {shareUsageData?.TotalOutstaningCitation && (
              <p style={{ marginTop: 10, marginLeft: 25 }}>
                <b>Citation: </b>
                <CitationLink
                  citation={shareUsageData?.TotalOutstaningCitation}
                  ticker={shareUsageData?.Ticker}
                  reportYear={reportFiscalYear}
                />
              </p>
            )}
            <div style={{ margin: 25 }} />
            {/* Burn Rate */}
            <h2 style={{ textAlign: "center" }}>
              <Tooltip title="Burn rate is defined by the sum of all awards granted in the report year divided by the total common shares outstanding. Expressed as a percentage.">
                <span style={{ cursor: "help" }}>
                  {FYneeded} Burn (Run) Rate:
                </span>
              </Tooltip>{" "}
              {equityPoolData?.burnRate}%
            </h2>
            {/* <h2>Adjusted Burn (Run) Rate: {adjustedBurnRate}%</h2> */}
            <h2 style={{ textAlign: "center" }}>
              {" "}
              <Tooltip title="Plan Overhang is defined by the sum of all awards outstanding in the report year divided by the total common shares outstanding. Expressed as a percentage.">
                <span style={{ cursor: "help" }}>
                  {FYneeded} Plan Overhang:
                </span>
              </Tooltip>{" "}
              {equityPoolData?.planOverhang}%
            </h2>
          </div>
        )}

        {/* TODO:  Consider removing reportFiscalYear.includes(FYneeded) if you want to view policy changes over time. (Warning: results may not be conisitent year-to-year) */}
        {allEquityPoolUsage?.[FYneeded]?.[companyInfo.Ticker]?.ShareUsageData
          ?.EvergreenPercentage &&
          reportFiscalYear.includes(FYneeded) && (
            <div>
              <h2>{FYneeded} Equity Pool Policies</h2>
              <Table
                columns={equityPolicyColumns}
                dataSource={getEquityPolicyData(FYneeded)}
                size={"small"}
                pagination={{
                  hideOnSinglePage: true,
                  defaultPageSize: 25,
                }}
              />
            </div>
          )}
      </div>
    );
  }

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (
      isInitialLoad &&
      allEquityPoolUsage?.[reportFiscalYear.replace("FY ", "")] &&
      !allEquityPoolUsage?.[reportFiscalYear.replace("FY ", "")]?.[
        companyInfo?.Ticker
      ]?.ShareUsageData
    ) {
      setReportFiscalYear("FY 2024");
      setIsInitialLoad(false);
    }
  }, [
    allEquityPoolUsage,
    reportFiscalYear,
    companyInfo,
    setReportFiscalYear,
    isInitialLoad,
  ]);

  const [equityPoolAverage, setEquityPoolAverage] = useState({});
  useEffect(() => {
    const filteredEquityPoolData = FYsNeeded.map((FY) =>
      getEquityPoolData(FY),
    ).filter((item) => item);

    const burnRate = Array.isArray(filteredEquityPoolData)
      ? (
          filteredEquityPoolData.reduce((prev, { burnRate }) => {
            const rate = parseFloat(burnRate);
            return prev + (isNaN(rate) ? 0 : rate);
          }, 0) / filteredEquityPoolData.length
        ).toFixed(2)
      : 0;

    const planOverhang = Array.isArray(filteredEquityPoolData)
      ? (
          filteredEquityPoolData.reduce((prev, { planOverhang }) => {
            const rate = parseFloat(planOverhang);
            return prev + (isNaN(rate) ? 0 : rate);
          }, 0) / filteredEquityPoolData.length
        ).toFixed(2)
      : 0;

    setEquityPoolAverage({
      burnRate,
      planOverhang,
      complete: filteredEquityPoolData.length >= 3,
      datapoints: filteredEquityPoolData.length,
    });
  }, [FYsNeeded, reportFiscalYear, EquityPoolAverageData, getEquityPoolData]);

  const equityPolicyColumns = [
    {
      title: "Policy",
      dataIndex: "Policy",
      key: "Policy",
      align: "center",
    },
    {
      title: "Value",
      dataIndex: "Value",
      key: "Value",
      align: "center",
      render: (perkValue) =>
        typeof perkValue === "boolean"
          ? perkValue === true
            ? "✓"
            : "N/A"
          : perkValue
            ? perkValue.toLocaleString()
            : "N/A",
    },
    {
      title: "Details",
      dataIndex: "Details",
      key: "Details",
      align: "center",
    },
    {
      title: "Citation",
      dataIndex: "Citation",
      key: "Citation",
      align: "center",
      render: (citation, col) => (
        <CitationLink
          citation={citation}
          ticker={col?.Ticker}
          reportYear={reportFiscalYear}
        />
      ),
    },
  ];
  const getOtherPerksPeerColumns = useMemo(() => {
    if (!allEquityPoolUsage) return [];

    const allPerksColumns = [
      {
        // title: "Ticker",
        title: <span style={{ fontSize: "10.5px" }}>Ticker</span>,
        dataIndex: "Ticker",
        key: "Ticker",
        align: "center",
        sorter: (a, b) => a.Ticker.localeCompare(b.Ticker),
        render: (text) => <span style={{ fontSize: "10.5px" }}>{text}</span>,
        width: 20,
      },
      {
        title: "Company Name",
        dataIndex: "CompanyName",
        key: "CompanyName",
        align: "center",
        sorter: (a, b) => a.CompanyName.localeCompare(b.CompanyName),
        render: (text) => <span style={{ fontSize: "10.5px" }}>{text}</span>,
      },
      {
        title: "Evergreen (%)",
        dataIndex: "EvergreenPercentage",
        key: "EvergreenPercentage",
        align: "center",
      },
      {
        title: "Individual Award Limit ($)",
        dataIndex: "IndividualAwardLimit",
        key: "IndividualAwardLimit",
        align: "center",
      },
      {
        title: "Director Award Limit ($)",
        dataIndex: "DirectorAwardLimit",
        key: "DirectorAwardLimit",
        align: "center",
      },
      {
        title: "Minimum Vesting Period (Months)",
        dataIndex: "MinimumVestingPeriod",
        key: "MinimumVestingPeriod",
        align: "center",
      },
      {
        title: "Liberal Share Recycling",
        dataIndex: "LiberalShareRecycling",
        key: "LiberalShareRecycling",
        align: "center",
      },
      {
        title: "Clawback Provisions",
        dataIndex: "Clawback",
        key: "Clawback",
        align: "center",
      },
    ];

    return allPerksColumns;
  }, [allEquityPoolUsage]);

  const getEquityPolicyData = useCallback(
    (FYneeded) => {
      if (!allEquityPoolUsage) return [];
      if (typeof allEquityPoolUsage?.[FYneeded] !== "object") {
        return [];
      }

      const allEquityPoolPoliciesForCompany = [];

      const companyEquityPoolData =
        allEquityPoolUsage?.[FYneeded][companyInfo?.Ticker];

      const equityPoolPolicies = getOtherPerksPeerColumns.filter(
        (item) => !["Ticker", "CompanyName"].includes(item.key),
      );

      for (const equityPolicy of equityPoolPolicies) {
        const equityPolicyDetails =
          companyEquityPoolData?.ShareUsageData?.[equityPolicy.key];

        const equityPolicyValue = equityPolicyDetails?.Value;
        const equityPolicyExplanation =
          typeof equityPolicyDetails?.Explanation === "string"
            ? toTitleCase(equityPolicyDetails?.Explanation)
            : "";
        const equityPolicyCitation = equityPolicyDetails?.Citation;

        const newPolicy = {
          Policy: equityPolicy.title,
          Value: equityPolicyValue || "",
          Details: equityPolicyExplanation || "",
          Citation: equityPolicyCitation || "",
        };

        allEquityPoolPoliciesForCompany.push(newPolicy);
      }
      return allEquityPoolPoliciesForCompany;
    },
    [companyInfo, allEquityPoolUsage, getOtherPerksPeerColumns],
  );

  const getOtherPerksPeerData = useCallback(() => {
    if (!allEquityPoolUsage) return [];

    const allPerksData = [];
    const perkOverview = {};
    if (
      typeof allEquityPoolUsage?.[reportFiscalYear.replace("FY ", "")] !==
      "object"
    ) {
      return [];
    }
    for (const [key, values] of Object.entries(
      allEquityPoolUsage?.[reportFiscalYear.replace("FY ", "")],
    )) {
      if (!values?.ShareUsageData?.EvergreenPercentage) {
        continue;
      }
      const newDataRow = {
        Ticker: values?.Ticker,
        CompanyName: values?.CompanyName,
      };
      const OtherPerks = getOtherPerksPeerColumns;
      for (const perk of OtherPerks) {
        // const titleCasePerk = toTitleCase(perk);
        const perkValue = values?.ShareUsageData?.[perk.key]?.Value;
        const perkExplanation =
          typeof values?.ShareUsageData?.[perk.key]?.Explanation === "string"
            ? toTitleCase(values?.ShareUsageData?.[perk.key]?.Explanation)
            : "";
        const perkCitation = values?.ShareUsageData?.[perk.key]?.Citation;

        if (perkValue) {
          newDataRow[perk.key] = (
            <Tooltip
              title={`${perkExplanation || ""} [${
                perkCitation ? (
                  <CitationLink
                    citation={perkCitation}
                    ticker={newDataRow?.Ticker}
                    reportYear={reportFiscalYear}
                  />
                ) : (
                  ""
                )
              }]`}
            >
              <span
                style={{ fontSize: "13px", width: "1000px", cursor: "help" }}
              >
                {typeof perkValue === "boolean"
                  ? perkValue === true
                    ? "✓"
                    : ""
                  : perkValue
                    ? perkValue.toLocaleString()
                    : ""}
              </span>
            </Tooltip>
          );

          if (!perkOverview?.[perk.key]) {
            perkOverview[perk.key] = 1;
          } else {
            perkOverview[perk.key] += 1;
          }
        }
      }
      allPerksData.push(newDataRow);
    }

    const overviewRow = {
      Ticker: <b>Percent</b>,
      CompanyName: emdash,
    };
    getOtherPerksPeerColumns
      .filter((perk) => !["CompanyName", "Ticker"].includes(perk.key))
      .forEach((perk) => {
        overviewRow[perk.key] = (
          <b>
            {(
              100 *
              ((perkOverview[perk.key] || 0) /
                Object.entries(
                  allEquityPoolUsage?.[reportFiscalYear.replace("FY ", "")],
                ).filter(
                  ([key, val]) => val?.ShareUsageData?.EvergreenPercentage,
                ).length)
            ).toFixed(1) + "%"}
          </b>
        );
      });
    allPerksData.unshift(overviewRow);

    return allPerksData;
  }, [allEquityPoolUsage, getOtherPerksPeerColumns, reportFiscalYear]);

  function onCheckboxChange(checkedValues) {
    if (checkedValues.includes("allAwards") && !allAwardsAccountedFor) {
      checkedValues = burnRateCalculationTypes.map((item) => item.value);
      setAllAwardsAccountedFor(true);
    } else if (allAwardsAccountedFor && !checkedValues.includes("allAwards")) {
      setAllAwardsAccountedFor(false);
      checkedValues = [];
      // TODO: Enable to select "all awards" if all other options selected
      // }  else if (
      //   checkedValues.length ===
      //     burnRateCalculationTypes.map((item) => item.value).length - 1 &&
      //   !checkedValues.includes("allAwards")
      // ) {
      //   checkedValues.push("allAwards");
      //   setAllAwardsAccountedFor(true);
    } else if (
      checkedValues.includes("allAwards") &&
      checkedValues.length !==
        burnRateCalculationTypes.map((item) => item.value).length
    ) {
      setAllAwardsAccountedFor(false);
      checkedValues = checkedValues.filter((item) => item !== "allAwards");
      //TODO: Enable to prevent the user from sleecting no opitons
      // } else if (!checkedValues.length) {
      //   checkedValues = ["RestrictedStockUnits"];
    }
    setBurnRateCalculationType(checkedValues);
  }

  return (
    <>
      {!isTier2 && !equityPoolAccess ? (
        <div
          className="comp-ai-section col-lg-9 executives "
          style={{ height: "100%", padding: 50 }}
        >
          <h3
            style={{
              textAlign: "center",
              fontWeight: "normal",
              fontSize: 22,
            }}
          >
            <p style={{ fontWeight: "bold" }}>
              This page is for C-Suite Comp&reg; Enterprise users only.
            </p>
            <br />
            <p>Contact your account manager to access Equity Pool Usage Data</p>
          </h3>
        </div>
      ) : (
        <>
          <div className="company-calculator-section col-lg-9 executives p-0">
            <RadioSelector
              options={FY_Options}
              state={reportFiscalYear}
              setState={setReportFiscalYear}
              heading="Report Fiscal Year:"
              style={{
                justifyContent: "center",
                marginBottom: FYmode ? 10 : 20,
              }}
              hoverText='"FY 2024" may be a mix of FYs from 2022-2024, but includes a more comprehensive set of company data. Select "FY 2025" For the most recent data, sorted by Fiscal Year.'
            />
            {FYmode && (
              <RadioSelector
                options={outStandingShareOptions}
                state={outstandingShareMode}
                setState={setOutstandingShareMode}
                heading="Outstanding Shares Period:"
                style={{ justifyContent: "center", marginBottom: 10 }}
                hoverText='Select "Average" to use an average of "Beginning" and "End" outstanding shares.'
              />
            )}
            {FYmode && (
              <RadioSelector
                options={outStandingShareDilutionOptions}
                state={outstandingShareDilutionMode}
                setState={setOutstandingShareDilutionMode}
                heading="Outstanding Shares Assumption:"
                style={{ justifyContent: "center", marginBottom: 10 }}
                hoverText='Select "Diluted" to include outstanding equity awards in the total shares outstanding.'
              />
            )}
            {FYmode && (
              <CustomTitleElement
                heading="Calculations Include:"
                hoverText={
                  "Select which awards to include in the burn rate and plan overhang calculation. Select 'All Awards' to include all award types (e.g. 'Other Awards')"
                }
                element={
                  <Checkbox.Group
                    options={burnRateCalculationTypes}
                    value={burnRateCalculationType}
                    onChange={onCheckboxChange}
                  />
                }
                style={{ justifyContent: "center" }}
              />
            )}

            {FYmode && (
              <RadioSelector
                options={averageDataOptions}
                state={averageDataMode}
                setState={setAverageDataMode}
                heading="Select Window:"
                style={{
                  justifyContent: "center",
                  marginBottom: 10,
                  marginTop: 10,
                }}
                disabledOptions={
                  !tickersWith3yearOption.includes(companyInfo?.Ticker)
                    ? ["3-Year Average"]
                    : []
                }
                optionsHoverTextMap={{
                  "3-Year Average": !tickersWith3yearOption.includes(
                    companyInfo?.Ticker,
                  )
                    ? "Contact your account manager to enable this option for this company and its peer group."
                    : null,
                }}
              />
            )}
            {!getEquityPoolUsageData() ? (
              <h2 style={{ textAlign: "center", marginTop: 50 }}>
                Equity Pool Usage Data for {companyInfo?.Ticker} Coming Soon for{" "}
                {reportFiscalYear}
              </h2>
            ) : (
              <div>
                {FYmode && averageDataMode.includes("Average") && (
                  <div style={{ textAlign: "center" }}>
                    <h2>
                      <Tooltip title="Burn rate is defined by the sum of all awards granted in the report year divided by the total common shares outstanding. Expressed as a percentage.">
                        <span style={{ cursor: "help" }}>
                          {FYsNeeded[2]}-{FYsNeeded[0]} Burn (Run) Rate:
                        </span>
                      </Tooltip>{" "}
                      {equityPoolAverage?.burnRate}%
                      {!equityPoolAverage?.complete ? "*" : ""}
                    </h2>
                    {/* <h2>Adjusted Burn (Run) Rate: {adjustedBurnRate}%</h2> */}
                    <h2>
                      {" "}
                      <Tooltip title="Plan Overhang is defined by the sum of all awards outstanding in the report year divided by the total common shares outstanding. Expressed as a percentage.">
                        <span style={{ cursor: "help" }}>
                          {FYsNeeded[2]}-{FYsNeeded[0]} Plan Overhang:
                        </span>
                      </Tooltip>{" "}
                      {equityPoolAverage?.planOverhang}%
                      {!equityPoolAverage?.complete ? "*" : ""}
                    </h2>
                    {!equityPoolAverage?.complete && (
                      <p style={{ marginTop: -10 }}>
                        *Not a complete 3-Year Average. Only reflects{" "}
                        {equityPoolAverage?.datapoints}/3 datapoints.
                      </p>
                    )}
                  </div>
                )}

                <EquityPoolResults
                  FYneeded={reportFiscalYear.replace("FY ", "")}
                />
                {FYmode && averageDataMode === "3-Year Average"
                  ? FYsNeeded.filter(
                      (FY) =>
                        FY.toString() !== reportFiscalYear.replace("FY ", ""),
                    ).map((FY) => (
                      <div>
                        <hr />
                        <EquityPoolResults FYneeded={FY} />
                      </div>
                    ))
                  : null}
              </div>
            )}
          </div>
          <div className="company-calculator-section col-lg-12 executives mt-5 p-0">
            <div className="company-calculator-section executives">
              <div style={{ margin: 10 }} />
              <hr />
              <h2 style={{ textAlign: "center" }}>Peer Group Comparison</h2>
              <p style={{ textAlign: "center" }}>
                Select a Comparison Group to compare to {companyInfo.Company}
                's Equity Pool Usage:
              </p>
              <CohortSelector
                heading={"Select Comparison Group:"}
                companyInfo={companyInfo}
                setCohortTableTickers={setCohortTableTickers}
                cohortTableSelector={cohortSelected}
                setCohortTableSelector={setCohortSelected}
                removeCustomCohort={true}
                additionalOptions={["None"]}
              />
              {cohortTableTickers.length ? (
                <div style={{ marginTop: 10 }}>
                  {FYmode && (
                    <CustomTitleElement
                      heading="Calculations Include:"
                      hoverText={
                        "Select which awards to include in the burn rate and plan overhang calculation. Select 'All Awards' to include all award types (e.g. 'Other Awards')"
                      }
                      element={
                        <Checkbox.Group
                          options={burnRateCalculationTypes}
                          value={burnRateCalculationType}
                          onChange={onCheckboxChange}
                        />
                      }
                    />
                  )}
                  {FYmode && averageDataMode === "3-Year Average" && (
                    <div>
                      <h2>
                        {FYsNeeded[2]}-{FYsNeeded[0]} Peer Group Average Rates
                      </h2>
                      <Table
                        columns={peerGroupAverageColumns}
                        dataSource={
                          getEquityPoolAveragePeerData()?.overviewData
                        }
                        size={"small"}
                        pagination={{
                          hideOnSinglePage: true,
                          defaultPageSize: 25,
                        }}
                      />
                    </div>
                  )}

                  <h2 style={{ marginTop: 10 }}>
                    {reportFiscalYear.includes("FY ")
                      ? reportFiscalYear.replace("FY ", "")
                      : ""}{" "}
                    Peer Group Equity Overview
                  </h2>
                  <Table
                    columns={peerGroupOverviewColumns}
                    dataSource={
                      getEquityPoolPeerData(reportFiscalYear.replace("FY ", ""))
                        ?.overviewData
                    }
                    size={"small"}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 25 }}
                  />
                  <div style={{ marginTop: 20 }} />
                  {allEquityPoolUsage?.[reportFiscalYear.replace("FY ", "")] &&
                    Object.values(
                      allEquityPoolUsage[reportFiscalYear.replace("FY ", "")],
                    ).some(
                      (item) => item?.ShareUsageData?.EvergreenPercentage,
                    ) && (
                      <div>
                        <h2>
                          {reportFiscalYear.includes("FY ")
                            ? reportFiscalYear.replace("FY ", "")
                            : ""}{" "}
                          Peer Group Equity Policies
                        </h2>
                        <p style={{ marginTop: -10 }}>
                          Hover over values to see details
                        </p>
                        <div style={{ marginTop: 10 }} />

                        <Table
                          columns={getOtherPerksPeerColumns.sort(
                            (a, b) =>
                              parseFloat(
                                getOtherPerksPeerData()
                                  .find((item) => item.CompanyName === emdash)
                                  [b.key]?.props?.children?.replace("%", "") ||
                                  b.key === "CompanyName"
                                  ? 1
                                  : 0,
                              ) -
                              parseFloat(
                                getOtherPerksPeerData()
                                  .find((item) => item.CompanyName === emdash)
                                  [a.key]?.props?.children?.replace("%", "") ||
                                  a.key === "CompanyName"
                                  ? 1
                                  : 0,
                              ),
                          )}
                          dataSource={getOtherPerksPeerData()}
                          size={"small"}
                          pagination={{
                            hideOnSinglePage: true,
                            defaultPageSize: 25,
                          }}
                          scroll={{ x: "max-content" }} // Enable horizontal scrolling
                        />
                      </div>
                    )}

                  <div style={{ marginTop: 20 }} />

                  <h2>
                    {reportFiscalYear.includes("FY ")
                      ? reportFiscalYear.replace("FY ", "")
                      : ""}{" "}
                    Peer Group Equity Breakdown
                  </h2>
                  <Table
                    columns={getEquityPoolColumns(true)}
                    dataSource={
                      getEquityPoolPeerData(reportFiscalYear.replace("FY ", ""))
                        ?.peerData
                    }
                    size={"small"}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 25 }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}
    </>
  );
}
